import React, { useLayoutEffect, useRef } from 'react';
import { useThree } from '@react-three/fiber';
import { BufferAttribute, BufferGeometry, ColorRepresentation, EdgesGeometry, LineSegments, Mesh } from 'three';

import { TrussEnvelope } from '@paragon/contract-library';

import { useAppSelector } from '../../../components/hooks';
import { calculationVerticesAndIndicesForBufferGeometry } from '../../../library/layout-geometry/mesh-data-operations';
import { getAllTrussEnvelopeSolids } from '../../../store/selectors/layoutDocumentSelectors';
import { NEVER_RAYCAST } from './TrussEnvelopeVisual';

export function TrussEnvelopeSolidVisual(props: {
  envelope: TrussEnvelope;
  color: ColorRepresentation;
  renderEdges: boolean;
}) {
  const invalidate = useThree((state) => state.invalidate);

  const solidMesh = useRef<Mesh | null>(null);
  const solidEdges = useRef<LineSegments | null>(null);

  const trussEnvelopeSolid = useAppSelector((state) => getAllTrussEnvelopeSolids(state)[props.envelope.guid]);
  const bufferGeometryData = trussEnvelopeSolid
    ? calculationVerticesAndIndicesForBufferGeometry({
        vertices: trussEnvelopeSolid.vertices,
        faces: trussEnvelopeSolid.facesByIndex.map((face) => ({ outer: face, inner: [] })),
      })
    : null;

  useLayoutEffect(() => {
    if (bufferGeometryData && solidMesh.current) {
      const geometry = new BufferGeometry();
      geometry.setAttribute('position', new BufferAttribute(bufferGeometryData.positions, 3));
      geometry.setIndex(new BufferAttribute(bufferGeometryData.indices, 1));
      solidMesh.current.geometry = geometry;

      let edgesGeometry: EdgesGeometry;
      if (solidEdges.current) {
        edgesGeometry = new EdgesGeometry(geometry);
        solidEdges.current.geometry = edgesGeometry;
      }

      invalidate();

      return () => {
        geometry.dispose();
        edgesGeometry?.dispose();
      };
    }
  });

  if (bufferGeometryData == null) return null;

  return (
    <group>
      <mesh ref={solidMesh}>
        <meshBasicMaterial color={props.color} />
      </mesh>

      {props.renderEdges && (
        <lineSegments
          ref={solidEdges}
          raycast={NEVER_RAYCAST} // events on edges is problematic with roof plane faces; best to only raycast the solid
        >
          <lineBasicMaterial color="black" linewidth={5} />
        </lineSegments>
      )}
    </group>
  );
}
